import type { Entity } from '@backstage/catalog-model';
import type { EntityContentGroupDefinitions } from '@backstage/plugin-catalog-react/alpha';
import type { ComponentType, FC, PropsWithChildren, ReactNode } from 'react';

import { DEFAULT_NAMESPACE } from '@backstage/catalog-model';
import {
  Content,
  Link,
  Progress,
  WarningPanel,
} from '@backstage/core-components';
import { useRouteRefParams } from '@backstage/frontend-plugin-api';
import {
  entityRouteRef,
  useAsyncEntity,
} from '@backstage/plugin-catalog-react';
import { getDisplayName } from '@ipen-portal/plugin-common';
import { getThemeOfEntity } from '@ipen-portal/plugin-common-react';
import { Alert } from '@mui/material';
import React from 'react';
import { tss } from 'tss-react/mui';

import { IpenPage } from './IpenPage';
import { PAGE_HEADER_SIZE, PageHeader } from './PageHeader';

const useStyles = tss.create(({ theme }) => ({
  content: {
    gridArea: 'content',
    padding: theme.spacing(3),
    [theme.breakpoints.up('xl')]: {
      padding: theme.spacing(3, 8),
    },
  },
  contentContainer: {
    margin: '0 auto',
    maxWidth: 2560,
    width: '100%',
  },
  header: {
    gridArea: 'header',
  },
  layout: {
    display: 'grid',
    gridTemplateAreas: "'header header' 'subNav content'",
    gridTemplateColumns: '1fr 100fr',
    gridTemplateRows: PAGE_HEADER_SIZE + 'px 100fr',
  },
  layoutNoNav: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
  },
  root: {
    flex: 1,
  },
  subNav: {
    gridArea: 'subNav',
  },
}));

type Props = {
  contextProvider?: ComponentType<PropsWithChildren<{ entity: Entity }>>[];
  groupDefinitions?: EntityContentGroupDefinitions;
  subNavigation?: ReactNode;
  subtitle?: string;
  title?: string;
  titleOrder?: Record<string, number>;
  toolbar?: React.ReactNode;
};

export const IpenEntityLayout: FC<PropsWithChildren<Props>> = ({
  children,
  contextProvider,
  groupDefinitions,
  subNavigation,
  subtitle,
  title,
  titleOrder,
  toolbar,
}) => {
  const { classes, cx } = useStyles();
  const { kind, name, namespace } = useRouteRefParams(entityRouteRef);
  const { entity, error, loading } = useAsyncEntity();

  const ns = namespace ?? entity?.metadata.namespace ?? '';
  let headerTitle = title ?? getDisplayName(entity, name);
  if (ns && ns !== DEFAULT_NAMESPACE) {
    headerTitle += ` in ${ns}`;
  }
  const logo = entity?.spec?.logo;
  const themeId = getThemeOfEntity(entity);
  let contentAndHeader = (
    <>
      <div className={classes.header}>
        {!loading && (
          <PageHeader
            entity={entity}
            groupDefinitions={groupDefinitions}
            logo={typeof logo === 'string' ? logo : undefined}
            subtitle={subtitle}
            title={headerTitle}
            titleOrder={titleOrder}
            toolbar={toolbar}
          />
        )}
      </div>
      <Content className={classes.content}>
        <div className={classes.contentContainer}>
          {loading && <Progress />}
          {error && <Alert severity="error">{error.toString()}</Alert>}
          {!loading && !error && !entity && (
            <WarningPanel title="Entity not found">
              There is no
              {' '}
              {kind}
              {' '}
              with the requested
              {' '}
              <Link to="https://backstage.io/docs/features/software-catalog/references">
                kind, namespace, and name
              </Link>
              .
            </WarningPanel>
          )}
          {children}
        </div>
      </Content>
    </>
  );
  if (entity) {
    contextProvider?.forEach(Provider => {
      contentAndHeader = (
        <Provider entity={entity}>{contentAndHeader}</Provider>
      );
    });
  }

  return (
    <IpenPage themeId={themeId} title={headerTitle}>
      <section
        aria-label={`${headerTitle} Page`}
        className={cx([
          classes.root,
          subNavigation ? classes.layout : classes.layoutNoNav,
        ])}
      >
        {subNavigation && <div className={classes.subNav}>{subNavigation}</div>}
        {contentAndHeader}
      </section>
    </IpenPage>
  );
};
